import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  I2gNgTrackingActionBuilder,
  I2gNgTrackingService,
  ITrackingAction,
  ITrackingObject,
} from 'i2g-ng-tracking';
import { Observable, Subject } from 'rxjs';
import {
  filter,
  flatMap,
  map,
  shareReplay,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { GeoLocationData } from '../../../app/store/models/geo-location-data.model';
import { AppStore } from '../../../app/store/stores';
import { LocalizationStore } from '../../../localization/store/stores';
import { TrackingScreenNamesEnum } from '../../../tracking/tracking.data';
import { IAccountResponseError } from '../../account.data';
import { AccountDispatcher } from '../../store/dispatchers';
import { Referer } from '../../store/models';
import { AccountStore } from '../../store/stores';
import { SignupBuilder } from './signup.builder';
import { IRouter } from 'i2g-ng-ui';

@Component({
  selector: 'app-signup',
  templateUrl: './signup.component.html',
  styleUrls: ['./signup.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [SignupBuilder],
})
export class SignupComponent implements OnInit, OnDestroy, AfterViewInit {
  private _destroyed: Subject<boolean> = new Subject<boolean>();
  private _termsOfServiceLink: any;
  private _privacyPolicyLink: any;
  private _trackingObject: ITrackingObject;

  error$: Observable<string | Error | IAccountResponseError>;
  loginRoute: IRouter;
  processing$: Observable<boolean>;
  referralDiscountMessage$: Observable<string>;
  referralCode: string;
  referer$: Observable<Referer>;
  trackingScreenName: TrackingScreenNamesEnum;
  trackingObject$: Observable<ITrackingObject>;
  emailSignUpTrackingAction: ITrackingAction;
  isLoadingGeoLocation$: Observable<boolean>;
  legalNote$: Observable<string>;

  constructor(
    private _signupBuilder: SignupBuilder,
    private _accountStore: AccountStore,
    private _accountDispatchers: AccountDispatcher,
    private _translateService: TranslateService,
    private _localizationStore: LocalizationStore,
    private _trackingService: I2gNgTrackingService,
    private _trackingActionBuilder: I2gNgTrackingActionBuilder,
    private _elementRef: ElementRef,
    private _appStore: AppStore
  ) {}

  ngOnInit() {
    this.trackingScreenName = TrackingScreenNamesEnum.account_sign_up;
    this.trackingObject$ = this._accountStore.getTrackingObject().pipe(
      takeUntil(this._destroyed),
      tap(
        (trackingObject: ITrackingObject) =>
          (this._trackingObject = trackingObject)
      ),
      shareReplay(1)
    );
    this.emailSignUpTrackingAction = this._trackingActionBuilder.buildButtonTapped(
      'sign_up',
      this.trackingScreenName
    );

    this.referer$ = this._accountStore.getReferer().pipe(
      takeUntil(this._destroyed),
      filter((x) => !!x),
      shareReplay(1)
    );

    this.processing$ = this._accountStore
      .isAccountRegistering()
      .pipe(takeUntil(this._destroyed), shareReplay(1));

    this.error$ = this._accountStore.getAccountError().pipe(
      takeUntil(this._destroyed),
      map((error: Error | IAccountResponseError) => {
        if (error) {
          return error instanceof Error ? error.message : error.description;
        }
        return error;
      }),
      shareReplay(1)
    );

    this.referralDiscountMessage$ = this.referer$.pipe(
      map((referer: Referer) =>
        this._translateService.instant(
          'SIGNUP.SUBSCRIPTION_DISCOUNT_WITH_COMPANY_NAME',
          {
            companyName: referer.name || referer.email,
          }
        )
      )
    );

    this.referer$.subscribe(
      (referer) => (this.referralCode = referer ? referer.referralCode : null)
    );

    this.loginRoute = this._signupBuilder.buildLoginRoute();

    this.legalNote$ = this._appStore.isLoadingGeoLocation().pipe(
      filter((isLoading) => !isLoading),
      flatMap(() => this._appStore.getGeoLocation()),
      map((geoLocation: GeoLocationData) => {
        return geoLocation && geoLocation.country.isoCode === 'US'
          ? 'SIGNUP.CCPA_LEGAL_NOTE'
          : 'SIGNUP.LEGAL_NOTE';
      }),
      shareReplay(1)
    );
  }

  ngAfterViewInit() {
    this._trackingService.trackPage(this.trackingScreenName);

    this._termsOfServiceLink = this._elementRef.nativeElement.querySelector(
      '.external-link.terms-of-service'
    );
    this._privacyPolicyLink = this._elementRef.nativeElement.querySelector(
      '.external-link.privacy-policy'
    );

    if (this._termsOfServiceLink) {
      this._termsOfServiceLink.addEventListener(
        'click',
        this.termsOfServiceClickHandler
      );
    }

    if (this._privacyPolicyLink) {
      this._privacyPolicyLink.addEventListener(
        'click',
        this.privacyPolicyClickHandler
      );
    }
  }

  createAccountByEmailAndPassword(email: string, password: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale: string) =>
        this._accountDispatchers.registerAccountByEmail(
          email,
          password,
          locale,
          this.trackingScreenName
        )
      );
  }

  createAccountByGoogle(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.registerAccountByGoogle(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  createAccountByFacebook(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.registerAccountByFacebook(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  createAccountByApple(email: string, idToken: string) {
    this.processing$
      .pipe(
        filter((processing) => !processing),
        flatMap(() => this._localizationStore.getCurrentLocale()),
        take(1)
      )
      .subscribe((locale) =>
        this._accountDispatchers.registerAccountByApple(
          email,
          idToken,
          locale,
          this.trackingScreenName
        )
      );
  }

  setErrorMessage(error: string) {
    this._accountDispatchers.registerAccountError(
      new Error(error),
      this.trackingScreenName
    );
  }

  termsOfServiceClickHandler = () => {
    this._trackingService.trackEvent(
      this._trackingObject,
      this._trackingActionBuilder.buildButtonTapped(
        'terms_of_service',
        this.trackingScreenName
      )
    );
  };

  privacyPolicyClickHandler = () => {
    this._trackingService.trackEvent(
      this._trackingObject,
      this._trackingActionBuilder.buildButtonTapped(
        'privacy_policy',
        this.trackingScreenName
      )
    );
  };

  ngOnDestroy() {
    this._destroyed.next(true);
    this._destroyed.unsubscribe();

    if (this._termsOfServiceLink) {
      this._termsOfServiceLink.removeEventListener(
        'click',
        this.termsOfServiceClickHandler
      );
    }

    if (this._privacyPolicyLink) {
      this._privacyPolicyLink.removeEventListener(
        'click',
        this.privacyPolicyClickHandler
      );
    }
  }
}
